<script setup>
import { ref } from 'vue';
import 'toastify-js/src/toastify.css';
import {customToast} from "@/globals/utils/customToast";
import * as carService from "@/services/carService";
import BackButtonComponent from "@/components/utils/BackButtonComponent.vue";
import DefaultButton from "@/components/utils/DefaultButton.vue";

// Form data
const carForm = ref({
  brand: '',
  model: '',
  licensePlate: '',
  passengerCapacity: '',
  luggageCapacity: '',
  carType: '',
});

// Car type options
const carTypes = [
  {
    value: 'van',
    label: 'Van',
  },
  {
    value: 'sedan',
    label: 'Sedan',
  },
  {
    value: 'suv',
    label: 'SUV',
  },
  {
    value: 'truck',
    label: 'Truck',
  },
  {
    value: 'mini_van',
    label: 'Mini Van',
  },
];

// Form submission handler
const submitForm = async () => {
  // Validate required fields
  if (
      !carForm.value.brand ||
      !carForm.value.model ||
      !carForm.value.licensePlate ||
      !carForm.value.passengerCapacity ||
      !carForm.value.luggageCapacity ||
      !carForm.value.carType
  ) {
    customToast('Please fill all required fields', 'error');
    return;
  }

  try {
    // Call the service to create a car
    const response = await carService.createCar(carForm.value);

    // Show success message
    customToast(response.message, 'success');

    // Reset the form
    carForm.value = {
      brand: '',
      model: '',
      licensePlate: '',
      passengerCapacity: '',
      luggageCapacity: '',
      carType: '',
    };
  } catch (error) {
    // Show error message
    customToast(`Failed to create car ${error}`, 'error');
  }
};
</script>

<template>
  <BackButtonComponent url="/cars" />
  <div class="max-w-4xl mx-auto mt-10 p-6 bg-white dark:bg-gray-900 shadow-md rounded-md">
    <h1 class="text-2xl font-bold mb-6 text-gray-800 dark:text-gray-100 text-center">Create New Car</h1>
    <form @submit.prevent="submitForm" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      <!-- Brand -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="brand">Brand</label>
        <input
            type="text"
            id="brand"
            v-model="carForm.brand"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
            placeholder="Toyota, Ford, etc."
        />
      </div>

      <!-- Model -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="model">Model</label>
        <input
            type="text"
            id="model"
            v-model="carForm.model"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
            placeholder="Corolla, Explorer, etc."
        />
      </div>

      <!-- License Plate -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="licensePlate">License Plate</label>
        <input
            type="text"
            id="licensePlate"
            v-model="carForm.licensePlate"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
            placeholder="ABC123"
        />
      </div>

      <!-- Passenger Capacity -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="passengerCapacity">Passenger Capacity</label>
        <input
            type="number"
            id="passengerCapacity"
            v-model="carForm.passengerCapacity"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
            placeholder="Number of passengers"
        />
      </div>

      <!-- Luggage Capacity -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="luggageCapacity">Luggage Capacity</label>
        <input
            type="number"
            id="luggageCapacity"
            v-model="carForm.luggageCapacity"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
            placeholder="Number of luggage"
        />
      </div>

      <!-- Car Type -->
      <div>
        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="carType">Car Type</label>
        <select
            id="carType"
            v-model="carForm.carType"
            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"
        >
          <option disabled value="">Select a type</option>
          <option v-for="type in carTypes" :key="type" :value="type.value">{{ type.label }}</option>
        </select>
      </div>

      <!-- Driver (Optional) -->
      <!--      <div class="md:col-span-2 lg:col-span-1">-->
      <!--        <label class="block text-gray-700 dark:text-gray-300 font-semibold mb-2" for="driver_id">Driver ID (Optional)</label>-->
      <!--        <input-->
      <!--            type="text"-->
      <!--            id="driver_id"-->
      <!--            v-model="carForm.driver_id"-->
      <!--            class="w-full p-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:bg-gray-800 dark:text-gray-100"-->
      <!--            placeholder="Driver ID"-->
      <!--        />-->
      <!--      </div>-->

      <!-- Submit Button -->
      <div class="md:col-span-2 lg:col-span-3 flex justify-center">
        <DefaultButton title="Save" type="submit" />
      </div>
    </form>
  </div>
</template>

<style scoped>
/* Additional styles if needed */
</style>
